"use client";

import { useState } from "react";
import { Bookmark, Trash2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import type { Message } from "@/hooks/use-chat";

interface BookmarkedMessage extends Message {
    originalIndex: number;
}

interface BookmarksDrawerProps {
    bookmarkedMessages: BookmarkedMessage[];
    onScrollToMessage: (index: number) => void;
    onRemoveBookmark: (index: number) => void;
    onClearAll: () => void;
}

// Format date for grouping
function formatDateGroup(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0 && messageDate.getDate() === now.getDate()) {
        return "Today";
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (
        messageDate.getDate() === yesterday.getDate() &&
        messageDate.getMonth() === yesterday.getMonth() &&
        messageDate.getFullYear() === yesterday.getFullYear()
    ) {
        return "Yesterday";
    }

    return messageDate.toLocaleDateString([], {
        month: "short",
        day: "numeric",
    });
}

// Get first line or portion of content for preview
function getContentPreview(content: string, maxLength: number = 120): string {
    // Remove markdown formatting for preview
    const plainText = content
        .replace(/[#*_`~]/g, "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Replace links with just text
        .replace(/\n+/g, " ")
        .trim();

    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + "...";
}

// Group messages by date
function groupByDate(
    messages: BookmarkedMessage[]
): Map<string, BookmarkedMessage[]> {
    const groups = new Map<string, BookmarkedMessage[]>();

    // Sort by timestamp descending (newest first)
    const sorted = [...messages].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    for (const message of sorted) {
        const dateKey = formatDateGroup(message.timestamp);
        const existing = groups.get(dateKey) || [];
        groups.set(dateKey, [...existing, message]);
    }

    return groups;
}

export function BookmarksDrawer({
    bookmarkedMessages,
    onScrollToMessage,
    onRemoveBookmark,
    onClearAll,
}: BookmarksDrawerProps) {
    const groupedMessages = groupByDate(bookmarkedMessages);
    const hasBookmarks = bookmarkedMessages.length > 0;

    // Control sheet open state
    const [isOpen, setIsOpen] = useState(false);

    // Track which message is expanded to show full content
    const [expandedMessageIndex, setExpandedMessageIndex] = useState<number | null>(null);

    const handleMessageClick = (message: BookmarkedMessage) => {
        if (expandedMessageIndex === message.originalIndex) {
            // If already expanded, collapse it
            setExpandedMessageIndex(null);
        } else {
            // Expand to show full message
            setExpandedMessageIndex(message.originalIndex);
        }
    };

    const handleGoToMessage = (index: number) => {
        // Close the drawer first, then scroll after a brief delay
        setIsOpen(false);
        setExpandedMessageIndex(null);
        // Small delay to let the drawer close animation start
        setTimeout(() => {
            onScrollToMessage(index);
        }, 150);
    };

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <Button
                    variant="outline"
                    size="icon"
                    className="relative"
                    aria-label="Saved tips"
                >
                    <Bookmark className="h-4 w-4" />
                    {hasBookmarks && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                            {bookmarkedMessages.length > 9 ? "9+" : bookmarkedMessages.length}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col pt-[env(safe-area-inset-top)]">
                <SheetHeader className="p-4 pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <Bookmark className="h-5 w-5 text-primary" />
                        <SheetTitle>Saved Messages</SheetTitle>
                    </div>
                </SheetHeader>

                <ScrollArea className="flex-1 bg-slate-50 dark:bg-slate-950/50">
                    {!hasBookmarks ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-6 space-y-4">
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                <Bookmark className="h-8 w-8 text-primary/60" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-lg font-medium text-foreground">No saved messages</h3>
                                <p className="text-sm text-muted-foreground/80 max-w-[250px] mx-auto leading-relaxed">
                                    Tap the bookmark icon on any AI response to save it here for later.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 space-y-6">
                            {/* Clear All button area */}
                            <div className="flex items-center justify-between px-1">
                                <span className="text-xs font-medium text-muted-foreground">
                                    {bookmarkedMessages.length} saved {bookmarkedMessages.length === 1 ? 'item' : 'items'}
                                </span>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10 px-2 transition-colors"
                                        >
                                            <Trash2 className="h-3 w-3 mr-1" />
                                            Clear All
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Clear all saved messages?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will remove all {bookmarkedMessages.length} saved messages.
                                                This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={onClearAll}>
                                                Clear All
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>

                            {Array.from(groupedMessages.entries()).map(
                                ([dateGroup, messages]) => (
                                    <div key={dateGroup} className="space-y-3">
                                        <div className="sticky top-0 z-10 bg-slate-50/95 dark:bg-slate-950/95 backdrop-blur-sm py-2 px-1">
                                            <div className="text-xs font-semibold text-primary/80 uppercase tracking-wider">
                                                {dateGroup}
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            {messages.map((message) => {
                                                const isExpanded = expandedMessageIndex === message.originalIndex;

                                                return (
                                                    <div
                                                        key={message.originalIndex}
                                                        className={`
                                                            group relative bg-card rounded-xl border transition-all duration-200 overflow-hidden
                                                            ${isExpanded
                                                                ? 'ring-1 ring-primary/20 shadow-md border-primary/20'
                                                                : 'hover:border-primary/30 hover:shadow-sm border-border/60 shadow-sm'
                                                            }
                                                        `}
                                                    >
                                                        {/* Accent border on left */}
                                                        <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${isExpanded ? 'bg-primary' : 'bg-primary/40 group-hover:bg-primary/60'}`} />

                                                        {/* Preview / Collapsed state */}
                                                        <div
                                                            className="p-4 pl-5 cursor-pointer"
                                                            onClick={() => handleMessageClick(message)}
                                                        >
                                                            <div className={`text-sm leading-relaxed text-card-foreground/90 ${isExpanded ? '' : 'line-clamp-3'}`}>
                                                                {isExpanded ? (
                                                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                                                        <MarkdownRenderer content={message.content} />
                                                                    </div>
                                                                ) : (
                                                                    <span>
                                                                        {getContentPreview(message.content, 140)}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Expand indicator when collapsed */}
                                                            {!isExpanded && (
                                                                <div className="flex justify-center mt-2 group-hover:translate-y-0.5 transition-transform">
                                                                    <ChevronDown className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary/60" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Action bar - shows when expanded */}
                                                        {isExpanded && (
                                                            <div className="flex items-center justify-end gap-2 px-3 py-2 bg-muted/30 border-t border-border/40">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-8 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/5"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleGoToMessage(message.originalIndex);
                                                                    }}
                                                                >
                                                                    Go to message
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-8 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/5"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onRemoveBookmark(message.originalIndex);
                                                                        if (isExpanded) setExpandedMessageIndex(null);
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
}
